import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useUnit } from 'effector-react';

import { apartmentsApi } from '../../api/apartments';
import { buildingsApi } from '../../api/buildings';
import { devicesApi } from '../../api/devices';
import { $canDeleteComplex, $isSettlementTenant } from '../../store/auth';
import { getObjectKindLabels } from '../../utils/complexKind';

function formatTs(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ru-RU');
  } catch {
    return String(value);
  }
}

function formatReading(value) {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

export default function OrphanDevicesPanel({ buildingId, onChanged }) {
  const [canManage, isSettlementTenant] = useUnit([$canDeleteComplex, $isSettlementTenant]);
  const labels = useMemo(
    () => getObjectKindLabels(isSettlementTenant),
    [isSettlementTenant]
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [apartmentOptions, setApartmentOptions] = useState([]);
  const [assignDevice, setAssignDevice] = useState(null);
  const [assignMode, setAssignMode] = useState('existing'); // existing | create
  const [apartmentId, setApartmentId] = useState('');
  const [number, setNumber] = useState('');
  const [suffix, setSuffix] = useState('');
  const [createBuilding, setCreateBuilding] = useState(false);
  const [buildingName, setBuildingName] = useState('');
  const [buildingAddress, setBuildingAddress] = useState('');
  const [saving, setSaving] = useState(false);
  const [dialogError, setDialogError] = useState('');

  const load = useCallback(async () => {
    if (!buildingId) return;
    setLoading(true);
    setError('');
    try {
      const data = await buildingsApi.getOrphanDevices(String(buildingId));
      setItems(data?.items || []);
      setTotal(Number(data?.total || 0));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалось загрузить непривязанные приборы');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAssign = async (device, mode = 'existing') => {
    setAssignDevice(device);
    setAssignMode(mode);
    setApartmentId('');
    setNumber('');
    setSuffix('');
    setCreateBuilding(false);
    setBuildingName('');
    setBuildingAddress('');
    setDialogError('');
    if (mode === 'existing') {
      try {
        const summary = await apartmentsApi.getBuildingSummary(String(buildingId));
        setApartmentOptions(
          (summary?.items || []).map((row) => ({
            id: String(row.apartment_id),
            label: `${row.display_number || row.number}${row.corp_name ? ` · ${row.corp_name}` : ''}`,
          }))
        );
      } catch {
        setApartmentOptions([]);
      }
    }
  };

  const closeAssign = () => {
    if (saving) return;
    setAssignDevice(null);
    setDialogError('');
  };

  const handleSave = async () => {
    if (!assignDevice) return;
    setSaving(true);
    setDialogError('');
    try {
      if (assignMode === 'existing') {
        if (!apartmentId) {
          setDialogError(`Выберите ${labels.apartment.toLowerCase()}`);
          return;
        }
        await devicesApi.assignApartment(String(assignDevice.id), { apartment_id: apartmentId });
      } else {
        if (!number.trim()) {
          setDialogError(`Укажите номер ${labels.apartment.toLowerCase()}`);
          return;
        }
        if (createBuilding && !buildingName.trim()) {
          setDialogError('Укажите название нового дома');
          return;
        }
        await devicesApi.assignNewApartment(String(assignDevice.id), {
          number: number.trim(),
          suffix: suffix.trim() || undefined,
          create_building: createBuilding,
          building_name: createBuilding ? buildingName.trim() : undefined,
          building_address: createBuilding ? (buildingAddress.trim() || undefined) : undefined,
        });
      }
      setAssignDevice(null);
      setDialogError('');
      await load();
      if (typeof onChanged === 'function') onChanged();
    } catch (err) {
      setDialogError(
        err?.response?.data?.detail
        || 'Не удалось привязать прибор'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rc-orphan-panel">
      <div style={{ marginBottom: 12 }}>
        <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600 }}>
          Непривязанные приборы
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {isSettlementTenant
            ? 'Приборы без дома. Показания принимаются, в отчёты не попадают. Выберите существующий дом или создайте новый.'
            : 'Приборы без квартиры. Показания принимаются, в отчёты не попадают. Выберите существующую квартиру или создайте новую.'}
        </Typography>
      </div>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

      {loading ? (
        <div className="rc-empty" style={{ padding: 40, textAlign: 'center' }}>
          <CircularProgress size={28} />
        </div>
      ) : total === 0 ? (
        <div className="rc-empty" style={{ padding: 32, textAlign: 'center', color: '#64748B' }}>
          Непривязанных приборов нет
        </div>
      ) : (
        <div className="rc-table-wrap">
          <table className="rc-table">
            <thead>
              <tr>
                <th>Серийный номер</th>
                <th>Тип</th>
                <th>DevEUI</th>
                <th>Статус</th>
                <th>Последнее показание</th>
                <th>Время</th>
                {canManage ? <th style={{ width: 220 }}>Действия</th> : null}
              </tr>
            </thead>
            <tbody>
              {items.map((device) => (
                <tr key={device.id}>
                  <td>{device.serial_number || '—'}</td>
                  <td>{device.system_type || '—'}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{device.dev_eui || '—'}</td>
                  <td>{device.status || '—'}</td>
                  <td>{formatReading(device.latest_reading_value)}</td>
                  <td>{formatTs(device.latest_reading_ts || device.last_seen_at)}</td>
                  {canManage ? (
                    <td>
                      <Stack direction="row" spacing={1}>
                        <button
                          type="button"
                          className="rc-btn rc-btn--sm"
                          onClick={() => openAssign(device, 'existing')}
                        >
                          Привязать
                        </button>
                        <button
                          type="button"
                          className="rc-btn rc-btn--sm"
                          onClick={() => openAssign(device, 'create')}
                        >
                          Создать
                        </button>
                      </Stack>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={Boolean(assignDevice)} onClose={closeAssign} fullWidth maxWidth="sm">
        <DialogTitle>
          {assignMode === 'existing'
            ? `Привязать к ${labels.apartment.toLowerCase()}`
            : `Создать ${labels.apartment.toLowerCase()} и привязать`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {assignDevice?.serial_number || assignDevice?.dev_eui || 'Прибор'}
          </Typography>
          {dialogError ? <Alert severity="error" sx={{ mb: 2 }}>{String(dialogError)}</Alert> : null}

          {assignMode === 'existing' ? (
            <TextField
              select
              fullWidth
              label={labels.apartment}
              value={apartmentId}
              onChange={(e) => setApartmentId(e.target.value)}
              margin="dense"
            >
              {apartmentOptions.length === 0 ? (
                <MenuItem value="" disabled>
                  Нет доступных {labels.premises.toLowerCase()}
                </MenuItem>
              ) : (
                apartmentOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
                ))
              )}
            </TextField>
          ) : (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              <TextField
                fullWidth
                label={`Номер ${labels.apartment.toLowerCase()}`}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                required
              />
              <TextField
                fullWidth
                label="Суффикс"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
              />
              {isSettlementTenant ? (
                <>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={createBuilding}
                        onChange={(e) => setCreateBuilding(e.target.checked)}
                      />
                    )}
                    label="Создать новый дом в посёлке"
                  />
                  {createBuilding ? (
                    <>
                      <TextField
                        fullWidth
                        label="Название дома"
                        value={buildingName}
                        onChange={(e) => setBuildingName(e.target.value)}
                        required
                      />
                      <TextField
                        fullWidth
                        label="Адрес дома"
                        value={buildingAddress}
                        onChange={(e) => setBuildingAddress(e.target.value)}
                      />
                    </>
                  ) : null}
                </>
              ) : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAssign} disabled={saving}>Отмена</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
